import type { LicenseCheckDto, LicenseInfoDto } from '@dbrain/shared';
import { Button, Dialog, Icons } from '@dbrain/ui';
import { useRef, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { isApiError } from '../../api/client.js';
import { isDetail, useLicense, useLicenseApply, useLicenseCheck } from '../../api/license.js';
import { formatBytes } from '@dbrain/app-core/format';
import { addDays, licenseDayAt, licenseRange } from '@dbrain/app-core/license-days';
import { ErrorAlert } from '../ErrorAlert.js';
import { ProductTags } from './parts.js';

/** the largest file the API takes (licenseFileBody) */
const MAX_BYTES = 16_384;

function Check({ ok, children, testId }: { ok: boolean; children: ReactNode; testId: string }) {
  return (
    <li className={ok ? 'license-check license-check--ok' : 'license-check license-check--bad'} data-testid={testId}>
      <span className="license-check__mark" aria-hidden="true">
        {ok ? <Icons.check size={14} /> : <Icons.close size={14} />}
      </span>
      <span>{children}</span>
    </li>
  );
}

/**
 * "Lisans dosyası yükle" (LisansYukle, D-121, D-126): the file is checked against this installation before
 * anything changes, and the dialog says what it found: the signature, this installation, the dates; a license
 * that starts later "kendiliğinden başlar", and in the free trial what becomes of it (D-133). Only a file that
 * passes can be applied. Super admins only.
 */
export function LicenseUploadDialog({ onClose, onApplied }: { onClose: () => void; onApplied?: (license: LicenseInfoDto) => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const license = useLicense().data;
  const detail = isDetail(license) ? license : undefined;
  const check = useLicenseCheck();
  const apply = useLicenseApply();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; size: number; text: string } | null>(null);
  const [dragging, setDragging] = useState(false);

  const pick = async (picked: File | undefined) => {
    if (!picked) return;
    apply.reset();
    check.reset();
    // a file this large is no license: it is not sent
    const text = picked.size > MAX_BYTES ? '' : await picked.text();
    setFile({ name: picked.name, size: picked.size, text });
    if (text) check.mutate(text);
  };

  const result: LicenseCheckDto | undefined = file && !file.text ? { ok: false, problem: 'format' } : check.data;
  const shown = result?.ok ? result.license : result?.license;
  const installed = result?.ok ? detail?.history.find((row) => row.licenseId === result.license.licenseId) : undefined;
  const signatureOk = result ? result.ok || result.problem === 'installation' || result.problem === 'expired' : false;
  const installationOk = result ? result.ok || result.problem === 'expired' : false;
  // the license in force ends the day before this one starts: no gap
  const follows = result?.ok && result.startsLater && license?.endsOn ? addDays(license.endsOn, 1) === result.license.validFrom : false;
  // in the free trial (D-133): when this license starts and what becomes of the trial
  const trial = license?.status === 'trial' ? license.trial : null;
  const trialNote =
    result?.ok && trial
      ? !result.startsLater
        ? 'license.upload.trialNow'
        : result.license.validFrom === addDays(trial.until, 1)
          ? 'license.upload.trialFollows'
          : result.license.validFrom <= trial.until
            ? 'license.upload.trialCut'
            : 'license.upload.trialGap'
      : null;

  const submit = () => {
    if (!file?.text || !result?.ok) return;
    const applied = result.license;
    apply.mutate(file.text, {
      onSuccess: () => {
        onApplied?.(applied);
        onClose();
      },
      // refused after all (the clock turned, another upload): the check says why
      onError: (error) => {
        if (isApiError(error) && error.status === 422) check.mutate(file.text);
      },
    });
  };

  return (
    <Dialog
      title={t('license.upload.title')}
      closeLabel={t('common.close')}
      onClose={onClose}
      data-testid="license-upload-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} data-testid="license-upload-cancel">
            {t('common.cancel')}
          </Button>
          <Button busy={apply.isPending} disabled={!result?.ok || Boolean(installed) || check.isPending} onClick={submit} data-testid="license-upload-apply">
            {t('license.upload.apply')}
          </Button>
        </>
      }
    >
      <input
        ref={input}
        type="file"
        accept=".oppy-lisans,application/json"
        className="ui-visually-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          void pick(picked);
        }}
        data-testid="license-upload-file"
      />
      {file ? (
        <div className="license-file" data-testid="license-upload-picked">
          <span className="license-file__icon" aria-hidden="true">
            <Icons.key size={18} />
          </span>
          <span className="license-file__text">
            <span className="license-file__name">{file.name}</span>
            <span className="muted">{t('license.upload.picked', { size: formatBytes(file.size, locale) })}</span>
          </span>
          <Button size="sm" variant="secondary" onClick={() => input.current?.click()} data-testid="license-upload-again">
            {t('license.upload.pickAgain')}
          </Button>
        </div>
      ) : (
        <div
          className={dragging ? 'license-drop license-drop--over' : 'license-drop'}
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void pick(e.dataTransfer.files[0]);
          }}
          data-testid="license-upload-drop"
        >
          <Icons.upload size={20} />
          <p>{t('license.upload.drop')}</p>
          <Button size="sm" variant="secondary" onClick={() => input.current?.click()} data-testid="license-upload-pick">
            {t('license.upload.pick')}
          </Button>
        </div>
      )}

      {check.isPending ? (
        <p className="muted" role="status">
          {t('license.upload.checking')}
        </p>
      ) : null}

      {result && !check.isPending ? (
        <div role="status" className="license-result" data-testid="license-upload-result" data-ok={result.ok ? 'true' : 'false'} data-problem={result.ok ? undefined : result.problem}>
          <ul className="license-checks">
            {signatureOk ? <Check ok testId="license-check-signature">{t('license.upload.signatureOk')}</Check> : null}
            {installationOk && shown ? (
              <Check ok testId="license-check-installation">
                {t('license.upload.installationOk')} <code>{result.ok ? result.installationId : (detail?.installationId ?? '')}</code>
              </Check>
            ) : null}
            {!result.ok ? (
              <Check ok={false} testId="license-check-problem">
                {t(`license.upload.problems.${result.problem}`)}
              </Check>
            ) : null}
          </ul>
          {!result.ok && result.problem === 'installation' && detail ? (
            <p className="muted license-result__hint">{t('license.upload.installationHint', { id: detail.installationId })}</p>
          ) : null}
          {shown ? (
            <dl className="license-facts license-facts--panel">
              <div>
                <dt>{t('license.facts.owner')}</dt>
                <dd>{shown.customer}</dd>
              </div>
              <div>
                <dt>{t('license.facts.users')}</dt>
                <dd>{t(shown.kind === 'trial' ? 'license.facts.usersTrial' : 'license.facts.usersFull')}</dd>
              </div>
              <div>
                <dt>{t('license.facts.products')}</dt>
                <dd>
                  <ProductTags products={shown.products} />
                </dd>
              </div>
              <div>
                <dt>{t('license.facts.validity')}</dt>
                <dd data-testid="license-upload-validity">{licenseRange(shown.validFrom, shown.validUntil, locale)}</dd>
              </div>
            </dl>
          ) : null}
          {installed ? (
            <p className="license-note" data-testid="license-upload-installed">
              <Icons.info size={16} />
              {t('license.upload.installed')}
            </p>
          ) : result.ok && trial && trialNote ? (
            <p className="license-note" data-testid={result.startsLater ? 'license-upload-later' : 'license-upload-now'}>
              <Icons.info size={16} />
              <span>
                <Trans
                  i18nKey={trialNote}
                  values={{ day: licenseDayAt(result.license.validFrom, locale, true), ends: licenseDayAt(trial.until, locale, true) }}
                  components={{ b: <strong /> }}
                />
              </span>
            </p>
          ) : result.ok && result.startsLater ? (
            <p className="license-note" data-testid="license-upload-later">
              <Icons.info size={16} />
              <span>
                <Trans
                  i18nKey={follows ? 'license.upload.follows' : 'license.upload.startsOn'}
                  values={{ day: licenseDayAt(result.license.validFrom, locale, true) }}
                  components={{ b: <strong /> }}
                />
              </span>
            </p>
          ) : null}
        </div>
      ) : null}
      <ErrorAlert error={check.error ?? (isApiError(apply.error) && apply.error.status === 422 ? null : apply.error)} />
    </Dialog>
  );
}
